import { DocService, DocsService } from '@affine/core/modules/doc';
import { AppThemeService } from '@affine/core/modules/theme';
import type { Container } from '@blocksuite/affine/global/di';
import { ColorScheme } from '@blocksuite/affine/model';
import {
  type ThemeExtension,
  ThemeExtensionIdentifier,
} from '@blocksuite/affine/shared/services';
import {
  createSignalFromObservable,
  type Signal,
} from '@blocksuite/affine/shared/utils';
import { LifeCycleWatcher, StdIdentifier } from '@blocksuite/affine/std';
import { type FrameworkProvider } from '@toeverything/infra';
import type { Observable } from 'rxjs';
import { combineLatest, map, of } from 'rxjs';

// When embedded (?embed or ?theme=light|dark), force the canvas theme and
// ignore per-doc edgelessColorTheme so the embed host controls appearance.
function getEmbedForcedTheme(): ColorScheme | null {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  const themeParam = params.get('theme');
  if (themeParam === 'dark') return ColorScheme.Dark;
  if (themeParam === 'light') return ColorScheme.Light;
  if (params.has('embed')) return ColorScheme.Light;
  return null;
}

export function getThemeExtension(
  framework: FrameworkProvider
): typeof LifeCycleWatcher {
  class AffineThemeExtension
    extends LifeCycleWatcher
    implements ThemeExtension
  {
    static override readonly key = 'affine-theme';

    private readonly themes: Map<string, Signal<ColorScheme>> = new Map();

    protected readonly disposables: (() => void)[] = [];

    static override setup(di: Container) {
      super.setup(di);
      di.override(ThemeExtensionIdentifier, AffineThemeExtension, [
        StdIdentifier,
      ]);
    }

    getAppTheme() {
      const keyName = 'app-theme';
      const cache = this.themes.get(keyName);
      if (cache) return cache;

      const forced = getEmbedForcedTheme();
      const theme$: Observable<ColorScheme> = forced
        ? of(forced)
        : framework
            .get(AppThemeService)
            .appTheme.theme$.map(theme => {
              return theme === ColorScheme.Dark
                ? ColorScheme.Dark
                : ColorScheme.Light;
            });
      const { signal: themeSignal, cleanup } =
        createSignalFromObservable<ColorScheme>(theme$, forced ?? ColorScheme.Light);
      this.disposables.push(cleanup);
      this.themes.set(keyName, themeSignal);
      return themeSignal;
    }

    getEdgelessTheme(docId?: string) {
      const doc =
        (docId && framework.get(DocsService).list.doc$(docId).getValue()) ||
        framework.get(DocService).doc;

      const cache = this.themes.get(doc.id);
      if (cache) return cache;

      const forced = getEmbedForcedTheme();
      const appTheme$ = framework.get(AppThemeService).appTheme.theme$;
      const docTheme$ = doc.properties$.map(
        props => props.edgelessColorTheme || 'system'
      );
      const theme$: Observable<ColorScheme> = forced
        ? of(forced)
        : combineLatest([appTheme$, docTheme$]).pipe(
            map(([appTheme, docTheme]) => {
              const theme = docTheme === 'system' ? appTheme : docTheme;
              return theme === ColorScheme.Dark
                ? ColorScheme.Dark
                : ColorScheme.Light;
            })
          );
      const { signal: themeSignal, cleanup } =
        createSignalFromObservable<ColorScheme>(theme$, forced ?? ColorScheme.Light);
      this.disposables.push(cleanup);
      this.themes.set(doc.id, themeSignal);
      return themeSignal;
    }

    override unmounted() {
      this.dispose();
    }

    dispose() {
      this.disposables.forEach(dispose => dispose());
    }
  }

  return AffineThemeExtension;
}
